/**
 * Platform detection — web vs Tauri vs unknown, online status.
 */

export type Platform = 'web' | 'tauri' | 'unknown';

let cachedPlatform: Platform | null = null;

/**
 * Detect the current platform at module init time.
 *
 * Checks `window.__TAURI__` which the Tauri webview injects at runtime.
 * Falls back to `'web'` when a window object exists, or `'unknown'` in SSR.
 */
export function getPlatform(): Platform {
  if (cachedPlatform) return cachedPlatform;

  if (typeof window === 'undefined') {
    cachedPlatform = 'unknown';
  } else if (window.__TAURI__) {
    cachedPlatform = 'tauri';
  } else {
    cachedPlatform = 'web';
  }

  return cachedPlatform;
}

/** Convenience shortcut for `getPlatform() === 'tauri'`. */
export function isTauri(): boolean {
  return getPlatform() === 'tauri';
}

/** Navigator fields used to detect iOS/iPadOS WebKit (Safari tab or WKWebView). */
export type NavigatorLike = {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
  hardwareConcurrency?: number;
  deviceMemory?: number;
};

/**
 * True for iPhone/iPad/iPod, including iPadOS 13+ which reports a Macintosh UA
 * with a touch screen. Used to apply Safari canvas and GPU limits.
 */
export function isIosWebKit(nav?: NavigatorLike): boolean {
  const n = nav ?? (typeof navigator === 'undefined' ? undefined : navigator);
  if (!n) return false;
  const ua = n.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return n.platform === 'MacIntel' && (n.maxTouchPoints ?? 0) > 1;
}

/** Returns the current online status via `navigator.onLine`. */
export function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

/**
 * Subscribe to browser online/offline events.
 *
 * @param callback — invoked immediately with the current online status, then
 *   whenever the browser fires `online` or `offline`.
 * @returns a cleanup function that removes the event listeners.
 */
export function onOnlineChange(callback: (online: boolean) => void): () => void {
  if (typeof window === 'undefined') {
    // SSR no-op
    return () => {};
  }

  // Fire immediately
  callback(navigator.onLine);

  const handler = () => callback(navigator.onLine);
  window.addEventListener('online', handler);
  window.addEventListener('offline', handler);

  return () => {
    window.removeEventListener('online', handler);
    window.removeEventListener('offline', handler);
  };
}
